import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  Shield,
  RefreshCw,
  CheckCircle,
  Mail,
  Phone,
  MapPin,
  Calendar,
  Activity,
  Trophy,
  FileText,
  Eye,
  Award,
  Plus,
  X
} from 'lucide-react';
import api from '../../services/api';
import toast from 'react-hot-toast';

// Mock user data
const mockUserProfile = {
  id: '1',
  name: 'Sarah Johnson',
  email: 'sarah.j@email.com',
  phone: '+1 (555) 123-4567',
  location: 'New York, NY',
  avatar: null,
  role: 'coach',
  sport: 'Tennis',
  status: 'active',
  verified: true,
  joinedDate: '2024-01-15T10:00:00Z',
  lastActive: '2024-11-01T08:30:00Z',
  achievements: [
    { id: '1', title: 'Tennis Coach Certification', date: '2024-01-20', sport: 'Tennis' },
    { id: '2', title: 'Regional Tournament Winner', date: '2024-03-15', sport: 'Tennis' }
  ],
  activityLog: [
    { id: '1', action: 'Logged in', timestamp: '2024-11-01T08:30:00Z' },
    { id: '2', action: 'Updated profile', timestamp: '2024-10-30T14:20:00Z' },
    { id: '3', action: 'Joined Tennis Academy community', timestamp: '2024-10-28T10:15:00Z' }
  ],
  verificationDocuments: [
    { id: '1', type: 'ID Verification', status: 'verified', uploadDate: '2024-01-15' },
    { id: '2', type: 'Coaching Certificate', status: 'verified', uploadDate: '2024-01-15' },
    { id: '3', type: 'Background Check', status: 'verified', uploadDate: '2024-01-16' }
  ]
};

const UserProfile: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<'overview' | 'achievements' | 'activity' | 'verification' | 'badges'>('overview');
  const [user, setUser] = useState<any>(mockUserProfile);
  const [loading, setLoading] = useState(false);
  const [showAddBadgeModal, setShowAddBadgeModal] = useState(false);
  const [newBadge, setNewBadge] = useState({
    type: 'champion',
    title: '',
    description: '',
    icon: '🏆',
    color: 'gold',
    earnedFor: ''
  });

  const getInitials = (name: string) => {
    return name.split(' ').map(n => n[0]).join('').toUpperCase();
  };

  const getStatusBadgeColor = (status: string) => {
    switch (status) {
      case 'active': return 'bg-green-100 text-green-800';
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      case 'blocked': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getRoleBadgeColor = (role: string) => {
    return role === 'coach' ? 'bg-gray-900 text-white' : 'bg-gray-500 text-white';
  };

  const getTimeAgo = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInMs = now.getTime() - date.getTime();
    const diffInHours = Math.floor(diffInMs / (1000 * 60 * 60));
    
    if (diffInHours < 1) return 'Just now';
    if (diffInHours < 24) return `${diffInHours} hours ago`;
    const diffInDays = Math.floor(diffInHours / 24);
    if (diffInDays < 7) return `${diffInDays} days ago`;
    return date.toLocaleDateString();
  };

  const handleBlockUser = () => {
    // Show confirmation dialog and handle block/unblock
    const action = user.status === 'blocked' ? 'unblock' : 'block';
    if (window.confirm(`${action.charAt(0).toUpperCase() + action.slice(1)} ${user.name}?`)) {
      setUser((prev: any) => ({
        ...prev,
        status: prev.status === 'blocked' ? 'active' : 'blocked'
      }));
    }
  };

  const handleResetPassword = () => {
    if (window.confirm(`Send password reset link to ${user.email}?`)) {
      // API call to reset password
      alert('Password reset link sent successfully!');
    }
  };

  const loadUserProfile = async () => {
    if (!id) return;
    try {
      setLoading(true);
      const response = await api.get(`/admin/users/${id}`);
      if (response.data.success) {
        setUser(response.data.data);
      }
    } catch (error) {
      console.error('Failed to load user profile:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddBadge = async () => {
    if (!id) return;
    
    try {
      const response = await api.post(`/admin/users/${id}/badges`, newBadge);
      if (response.data.success) {
        toast.success('Badge added successfully!');
        setShowAddBadgeModal(false);
        setNewBadge({
          type: 'champion',
          title: '',
          description: '',
          icon: '🏆',
          color: 'gold',
          earnedFor: ''
        });
        loadUserProfile();
      }
    } catch (error: any) {
      toast.error(error.response?.data?.error?.message || 'Failed to add badge');
    }
  };

  const handleRemoveBadge = async (badgeIndex: number) => {
    if (!id) return;
    if (!window.confirm('Remove this badge from user?')) return;

    try {
      const response = await api.delete(`/admin/users/${id}/badges/${badgeIndex}`);
      if (response.data.success) {
        toast.success('Badge removed successfully!');
        loadUserProfile();
      }
    } catch (error: any) {
      toast.error(error.response?.data?.error?.message || 'Failed to remove badge');
    }
  };

  useEffect(() => {
    if (id) {
      loadUserProfile();
    }
  }, [id]);

  const UserAvatar: React.FC<{ size?: 'sm' | 'md' | 'lg' | 'xl' }> = ({ size = 'xl' }) => {
    const sizeClasses = {
      sm: 'w-8 h-8 text-xs',
      md: 'w-12 h-12 text-sm',
      lg: 'w-16 h-16 text-lg',
      xl: 'w-20 h-20 text-2xl'
    };

    return (
      <div className={`${sizeClasses[size]} rounded-full flex items-center justify-center ${
        user.avatar ? '' : 'bg-gray-200 text-gray-600 font-medium'
      }`}>
        {user.avatar ? (
          <img src={user.avatar} alt={user.name} className="w-full h-full rounded-full object-cover" />
        ) : (
          getInitials(user.name)
        )}
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {/* Back Navigation */}
      <button
        onClick={() => navigate('/admin/users')}
        className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
      >
        <ArrowLeft className="w-4 h-4 mr-2" />
        Back to Users
      </button>

      {/* Page Title */}
      <h1 className="text-3xl font-bold text-gray-900">User Profile</h1>

      {/* User Profile Header */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex items-start justify-between">
          {/* User Info */}
          <div className="flex items-start space-x-6">
            <UserAvatar />
            <div className="flex-1">
              <div className="flex items-center space-x-3 mb-2">
                <h2 className="text-2xl font-bold text-gray-900">{user.name}</h2>
                {user.verified && (
                  <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                    <CheckCircle className="w-3 h-3 mr-1" />
                    Verified
                  </span>
                )}
              </div>
              
              <div className="flex items-center space-x-2 mb-4">
                <span className={`px-2 py-1 text-xs font-medium rounded-full ${getRoleBadgeColor(user.role)}`}>
                  {user.role.charAt(0).toUpperCase() + user.role.slice(1)}
                </span>
                <span className="px-2 py-1 text-xs font-medium bg-yellow-100 text-yellow-800 rounded-full">
                  {user.sport}
                </span>
                <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusBadgeColor(user.status)}`}>
                  {user.status.charAt(0).toUpperCase() + user.status.slice(1)}
                </span>
              </div>

              <div className="space-y-2 text-sm text-gray-600">
                <div className="flex items-center space-x-2">
                  <Mail className="w-4 h-4" />
                  <span>{user.email}</span>
                </div>
                <div className="flex items-center space-x-2">
                  <Phone className="w-4 h-4" />
                  <span>{user.phone}</span>
                </div>
                <div className="flex items-center space-x-2">
                  <MapPin className="w-4 h-4" />
                  <span>{user.location}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex items-center space-x-3">
            <button
              onClick={handleBlockUser}
              className={`inline-flex items-center px-4 py-2 border border-transparent rounded-md text-sm font-medium text-white ${
                user.status === 'blocked' 
                  ? 'bg-green-600 hover:bg-green-700' 
                  : 'bg-red-600 hover:bg-red-700'
              }`}
            >
              <Shield className="w-4 h-4 mr-2" />
              {user.status === 'blocked' ? 'Unblock User' : 'Block User'}
            </button>
            <button
              onClick={handleResetPassword}
              className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Reset Password
            </button>
          </div>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          {[
            { key: 'overview', label: 'Overview', icon: Eye },
            { key: 'achievements', label: 'Achievements', icon: Trophy },
            { key: 'activity', label: 'Activity', icon: Activity },
            { key: 'verification', label: 'Verification', icon: FileText },
            { key: 'badges', label: 'Badges', icon: Award }
          ].map(({ key, label, icon: Icon }) => (
            <button
              key={key}
              onClick={() => setActiveTab(key as any)}
              className={`py-2 px-1 border-b-2 font-medium text-sm flex items-center space-x-2 ${
                activeTab === key
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <Icon className="w-4 h-4" />
              <span>{label}</span>
            </button>
          ))}
        </nav>
      </div>

      {/* Tab Content */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        {activeTab === 'overview' && (
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Basic Information</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-500 mb-1">Email Address</label>
                <p className="text-sm text-gray-900">{user.email}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-500 mb-1">Joined Date</label>
                <div className="flex items-center space-x-2">
                  <Calendar className="w-4 h-4 text-gray-400" />
                  <p className="text-sm text-gray-900">{new Date(user.joinedDate).toLocaleDateString()}</p>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-500 mb-1">Phone Number</label>
                <p className="text-sm text-gray-900">{user.phone}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-500 mb-1">Last Active</label>
                <div className="flex items-center space-x-2">
                  <Activity className="w-4 h-4 text-gray-400" />
                  <p className="text-sm text-gray-900">{getTimeAgo(user.lastActive)}</p>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-500 mb-1">Location</label>
                <p className="text-sm text-gray-900">{user.location}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-500 mb-1">Account Status</label>
                <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusBadgeColor(user.status)}`}>
                  {user.status.charAt(0).toUpperCase() + user.status.slice(1)}
                </span>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'achievements' && (
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Achievements</h3>
            <div className="space-y-4">
              {user.achievements.map((achievement: any) => (
                <div key={achievement.id} className="flex items-center space-x-4 p-4 border border-gray-200 rounded-lg">
                  <div className="w-12 h-12 bg-yellow-100 rounded-full flex items-center justify-center">
                    <Trophy className="w-6 h-6 text-yellow-600" />
                  </div>
                  <div className="flex-1">
                    <h4 className="font-medium text-gray-900">{achievement.title}</h4>
                    <p className="text-sm text-gray-500">
                      {achievement.sport} • {new Date(achievement.date).toLocaleDateString()}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {activeTab === 'activity' && (
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Recent Activity</h3>
            <div className="space-y-4">
              {user.activityLog.map((activity: any) => (
                <div key={activity.id} className="flex items-start space-x-4 p-4 border border-gray-200 rounded-lg">
                  <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                    <Activity className="w-4 h-4 text-blue-600" />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm text-gray-900">{activity.action}</p>
                    <p className="text-xs text-gray-500">{getTimeAgo(activity.timestamp)}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {activeTab === 'verification' && (
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Verification Documents</h3>
            <div className="space-y-4">
              {user.verificationDocuments.map((doc: any) => (
                <div key={doc.id} className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
                  <div className="flex items-center space-x-4">
                    <div className="w-12 h-12 bg-gray-100 rounded-lg flex items-center justify-center">
                      <FileText className="w-6 h-6 text-gray-600" />
                    </div>
                    <div>
                      <h4 className="font-medium text-gray-900">{doc.type}</h4>
                      <p className="text-sm text-gray-500">
                        Uploaded: {new Date(doc.uploadDate).toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-3">
                    <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                      doc.status === 'verified' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                    }`}>
                      {doc.status.charAt(0).toUpperCase() + doc.status.slice(1)}
                    </span>
                    <button className="text-blue-600 hover:text-blue-800 text-sm font-medium">
                      View
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {activeTab === 'badges' && (
          <div>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Badges</h3>
              <button
                onClick={() => setShowAddBadgeModal(true)}
                className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Badge
              </button>
            </div>
            
            <div className="space-y-3">
              {user.badges && user.badges.filter((b: any) => b.isActive).length > 0 ? (
                user.badges.filter((b: any) => b.isActive).map((badge: any, index: number) => (
                  <div key={index} className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
                    <div className="flex items-center space-x-4">
                      <div className={`p-3 rounded-full ${
                        badge.color === 'gold' ? 'bg-gradient-to-r from-yellow-400 to-yellow-600' :
                        badge.color === 'silver' ? 'bg-gradient-to-r from-gray-300 to-gray-500' :
                        badge.color === 'bronze' ? 'bg-gradient-to-r from-orange-400 to-orange-600' :
                        badge.color === 'blue' ? 'bg-gradient-to-r from-blue-400 to-blue-600' :
                        badge.color === 'purple' ? 'bg-gradient-to-r from-purple-400 to-purple-600' :
                        badge.color === 'green' ? 'bg-gradient-to-r from-green-400 to-green-600' :
                        'bg-gradient-to-r from-red-400 to-red-600'
                      }`}>
                        <span className="text-white text-xl">{badge.icon || '🏆'}</span>
                      </div>
                      <div>
                        <h4 className="font-medium text-gray-900">{badge.title}</h4>
                        {badge.description && (
                          <p className="text-sm text-gray-500">{badge.description}</p>
                        )}
                        {badge.earnedFor && (
                          <p className="text-xs text-gray-400 mt-1">Earned for: {badge.earnedFor}</p>
                        )}
                        <p className="text-xs text-gray-400 mt-1">
                          Earned: {new Date(badge.earnedAt).toLocaleDateString()}
                        </p>
                      </div>
                    </div>
                    <button
                      onClick={() => handleRemoveBadge(index)}
                      className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                      title="Remove Badge"
                    >
                      <X className="w-5 h-5" />
                    </button>
                  </div>
                ))
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <Award className="w-12 h-12 mx-auto mb-2 text-gray-300" />
                  <p>No badges assigned yet</p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Add Badge Modal */}
      {showAddBadgeModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Add Badge</h3>
              <button
                onClick={() => setShowAddBadgeModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Badge Type *</label>
                <select
                  value={newBadge.type}
                  onChange={(e) => setNewBadge(prev => ({ ...prev, type: e.target.value }))}
                  className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                >
                  <option value="champion">Champion</option>
                  <option value="elite_player">Elite Player</option>
                  <option value="rising_star">Rising Star</option>
                  <option value="certified_coach">Certified Coach</option>
                  <option value="achievement_master">Achievement Master</option>
                  <option value="community_leader">Community Leader</option>
                  <option value="top_performer">Top Performer</option>
                  <option value="dedicated_player">Dedicated Player</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Title *</label>
                <input
                  type="text"
                  value={newBadge.title}
                  onChange={(e) => setNewBadge(prev => ({ ...prev, title: e.target.value }))}
                  placeholder="Badge title (auto-filled if empty)"
                  className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <textarea
                  value={newBadge.description}
                  onChange={(e) => setNewBadge(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="Optional description"
                  rows={2}
                  className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Icon (Emoji)</label>
                <input
                  type="text"
                  value={newBadge.icon}
                  onChange={(e) => setNewBadge(prev => ({ ...prev, icon: e.target.value }))}
                  placeholder="🏆"
                  className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Color *</label>
                <select
                  value={newBadge.color}
                  onChange={(e) => setNewBadge(prev => ({ ...prev, color: e.target.value }))}
                  className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                >
                  <option value="gold">Gold</option>
                  <option value="silver">Silver</option>
                  <option value="bronze">Bronze</option>
                  <option value="blue">Blue</option>
                  <option value="purple">Purple</option>
                  <option value="green">Green</option>
                  <option value="red">Red</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Earned For</label>
                <input
                  type="text"
                  value={newBadge.earnedFor}
                  onChange={(e) => setNewBadge(prev => ({ ...prev, earnedFor: e.target.value }))}
                  placeholder="e.g., Winning National Championship"
                  className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>

            <div className="flex items-center justify-end space-x-3 mt-6">
              <button
                onClick={() => setShowAddBadgeModal(false)}
                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleAddBadge}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                Add Badge
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserProfile;